import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useState } from "react";
import {
  useCreateDocumentType,
  useUpdateDocumentType,
} from "../../../hooks/api/documentTypesApiHooks";

/**
 * Modal para criar/editar tipo de documento
 * @param {object} props
 * @param {boolean} props.isOpen - Se o modal está aberto
 * @param {function} props.onClose - Callback ao fechar
 * @param {object} props.documentType - Tipo de documento (se editando)
 * @param {boolean} props.isEditing - Se está em modo de edição
 * @param {function} props.onSuccess - Callback ao sucesso
 * @param {function} props.initializeLoadingToast - Função para inicializar toast
 * @param {function} props.updateLoadingToast - Função para atualizar toast
 */
export default function DocumentTypeFormModal({
  isOpen,
  onClose,
  documentType,
  isEditing,
  onSuccess,
  initializeLoadingToast,
  updateLoadingToast,
}) {
  // Estados do formulário
  const [formData, setFormData] = useState({
    name: "",
    category: "Técnico",
    allowedExtensions: [],
    maxSizeMB: 10,
    displayOrder: 1,
    description: "",
    folderPath: "Documentos",
    isActive: true,
  });

  const [extensionInput, setExtensionInput] = useState("");
  const [requestLoading, setRequestLoading] = useState(false);

  // Hooks de API
  const { mutateAsync: createType, isLoading: isCreating } = useCreateDocumentType();
  const { mutateAsync: updateType, isLoading: isUpdating } = useUpdateDocumentType();

  const isLoading = isCreating || isUpdating || requestLoading;

  // Categorias disponíveis
  const categories = ["Técnico", "Legal", "Cliente", "Administrativo"];

  // Extensões comuns
  const commonExtensions = [
    "pdf",
    "doc",
    "docx",
    "jpg",
    "jpeg",
    "png",
    "dwg",
    "dxf",
    "xlsx",
    "xls",
  ];

  // Carregar dados ao editar
  useEffect(() => {
    if (isEditing && documentType) {
      setFormData({
        name: documentType.name || "",
        category: documentType.category || "Técnico",
        allowedExtensions: documentType.allowedExtensions || [],
        maxSizeMB: documentType.maxSizeMB || 10,
        displayOrder: documentType.displayOrder || 1,
        description: documentType.description || "",
        folderPath: documentType.folderPath || "Documentos",
        isActive: documentType.isActive !== undefined ? documentType.isActive : true,
      });
    }
  }, [isEditing, documentType]);

  // Handlers
  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleAddExtension = (extension) => {
    const ext = extension.toLowerCase().replace(".", "");
    if (ext && !formData.allowedExtensions.includes(ext)) {
      setFormData((prev) => ({
        ...prev,
        allowedExtensions: [...prev.allowedExtensions, ext],
      }));
      setExtensionInput("");
    }
  };

  const handleRemoveExtension = (extension) => {
    setFormData((prev) => ({
      ...prev,
      allowedExtensions: prev.allowedExtensions.filter((ext) => ext !== extension),
    }));
  };

  const handleSubmit = async () => {
    // Validações
    if (!formData.name.trim()) {
      return;
    }
    if (formData.allowedExtensions.length === 0) {
      return;
    }
    if (formData.maxSizeMB <= 0) {
      return;
    }
    if (formData.displayOrder <= 0) {
      return;
    }

    let toastId;
    try {
      setRequestLoading(true);
      toastId = initializeLoadingToast({
        text: isEditing ? "Atualizando tipo de documento..." : "Criando tipo de documento...",
      });

      if (isEditing) {
        await updateType({
          id: documentType.id,
          data: formData,
        });
      } else {
        await createType(formData);
      }

      updateLoadingToast({
        id: toastId,
        text: isEditing
          ? "Tipo de documento atualizado com sucesso!"
          : "Tipo de documento criado com sucesso!",
        type: "success",
      });

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error("Erro ao salvar tipo:", error);
      const errorMessage =
        error.response?.data?.message ||
        `Erro ao ${isEditing ? "atualizar" : "criar"} tipo de documento`;
      updateLoadingToast({
        id: toastId,
        text: errorMessage,
        type: "error",
      });
    } finally {
      setRequestLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={!isLoading ? onClose : undefined} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {isEditing ? "Editar Tipo de Documento" : "Novo Tipo de Documento"}
        </Typography>
        {!isLoading && (
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {/* Nome */}
        <Box sx={{ mb: 2 }}>
          <TextField
            label="Nome *"
            fullWidth
            value={formData.name}
            onChange={handleChange("name")}
            placeholder="Ex: ART, Memorial Descritivo"
            disabled={isLoading}
          />
        </Box>

        {/* Categoria */}
        <Box sx={{ mb: 2 }}>
          <TextField
            label="Categoria *"
            select
            fullWidth
            value={formData.category}
            onChange={handleChange("category")}
            disabled={isLoading}
          >
            {categories.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {/* Extensões Permitidas */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Extensões Permitidas *
          </Typography>
          
          {/* Extensões Comuns */}
          <Box sx={{ mb: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {commonExtensions.map((ext) => (
              <Chip
                key={ext}
                label={ext.toUpperCase()}
                size="small"
                onClick={() => handleAddExtension(ext)}
                color={formData.allowedExtensions.includes(ext) ? "primary" : "default"}
                variant={formData.allowedExtensions.includes(ext) ? "filled" : "outlined"}
              />
            ))}
          </Box>

          {/* Input Customizado */}
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              size="small"
              fullWidth
              value={extensionInput}
              onChange={(e) => setExtensionInput(e.target.value)}
              placeholder="Digite uma extensão e pressione Enter"
              disabled={isLoading}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleAddExtension(extensionInput);
                }
              }}
            />
            <Button
              variant="outlined"
              onClick={() => handleAddExtension(extensionInput)}
              disabled={!extensionInput || isLoading}
            >
              Adicionar
            </Button>
          </Box>

          {/* Extensões Selecionadas */}
          {formData.allowedExtensions.length > 0 && (
            <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {formData.allowedExtensions.map((ext) => (
                <Chip
                  key={ext}
                  label={ext.toUpperCase()}
                  size="small"
                  onDelete={() => handleRemoveExtension(ext)}
                  color="primary"
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Tamanho Máximo */}
        <Box sx={{ mb: 2 }}>
          <TextField
            label="Tamanho Máximo (MB) *"
            type="number"
            fullWidth
            value={formData.maxSizeMB}
            onChange={handleChange("maxSizeMB")}
            disabled={isLoading}
            inputProps={{ min: 1, step: 1 }}
          />
        </Box>

        {/* Ordem de Exibição */}
        <Box sx={{ mb: 2 }}>
          <TextField
            label="Ordem de Exibição *"
            type="number"
            fullWidth
            value={formData.displayOrder}
            onChange={handleChange("displayOrder")}
            disabled={isLoading}
            inputProps={{ min: 1, step: 1 }}
            helperText="Define a ordem em que o tipo aparecerá na lista"
          />
        </Box>

        {/* Pasta OneDrive */}
        <Box sx={{ mb: 2 }}>
          <TextField
            label="Pasta OneDrive *"
            fullWidth
            value={formData.folderPath}
            onChange={handleChange("folderPath")}
            disabled={isLoading}
            helperText="Caminho da pasta no OneDrive onde o documento será salvo (ex: Art, Datasheets, Docs)"
            placeholder="Documentos"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
            💡 Sugestões: <strong>Art</strong>, <strong>Datasheets</strong>, <strong>Docs</strong>, <strong>Projeto SFVCR/Memorial</strong>, <strong>Projeto SFVCR/CAD</strong>, <strong>Solicitação</strong>
          </Typography>
        </Box>

        {/* Descrição */}
        <Box sx={{ mb: 2 }}>
          <TextField
            label="Descrição"
            multiline
            rows={3}
            fullWidth
            value={formData.description}
            onChange={handleChange("description")}
            placeholder="Descrição ou ajuda sobre este tipo de documento"
            disabled={isLoading}
          />
        </Box>

        {/* Ativo */}
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, isActive: e.target.checked }))
                }
                disabled={isLoading}
              />
            }
            label="Ativo"
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {isLoading ? "Salvando..." : isEditing ? "Atualizar" : "Criar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

